import type { ReactNode } from 'react';

/**
 * Inline-SVG иконки в стиле lucide (outline, 24×24).
 *
 * Держим набор своим, а не тянем библиотеку: используется полтора десятка
 * иконок, и они попадают в бандл целиком, без tree-shaking-сюрпризов.
 * Раньше в навигации стояли эмодзи — они выглядят по-разному на iOS,
 * Android и десктопе и ломают фирменный стиль.
 */
const PATHS = {
  sparkles: (
    <>
      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" />
    </>
  ),
  crown: <path d="M3 8l4 4 5-7 5 7 4-4v10H3V8z" />,
  female: (
    <>
      <circle cx="12" cy="8" r="5" />
      <path d="M12 13v8M9 18h6" />
    </>
  ),
  male: (
    <>
      <circle cx="10" cy="14" r="5" />
      <path d="M15 9l5-5m0 0h-4m4 0v4" />
    </>
  ),
  baby: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01M9 15c1 1 5 1 6 0" />
    </>
  ),
  drama: (
    <>
      <path d="M4 5h7v6a3.5 3.5 0 01-7 0V5z" />
      <path d="M13 9h7v4a3.5 3.5 0 01-7 0V9z" />
    </>
  ),
  watch: (
    <>
      <circle cx="12" cy="12" r="5" />
      <path d="M12 10v2l1.5 1M9 3h6M9 21h6" />
    </>
  ),
  shirt: <path d="M6 4l3-1 3 2 3-2 3 1 2 4-3 1v9H7v-9L4 8l2-4z" />,
  heart: (
    <path d="M12 21s-7-4.35-9.5-8.5C.8 9.5 2.5 6 6 6c2 0 3 1 4 2 1-1 2-2 4-2 3.5 0 5.2 3.5 3.5 6.5C19 16.65 12 21 12 21z" />
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6z" />
      <path d="M10 20a2 2 0 004 0" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-6-5.5-6-10a6 6 0 1112 0c0 4.5-6 10-6 10z" />
      <circle cx="12" cy="11" r="2" />
    </>
  ),
  chevron: <path d="M9 6l6 6-6 6" />,
  back: <path d="M15 6l-6 6 6 6" />,
  cart: (
    <>
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="18" cy="20" r="1.5" />
      <path d="M3 4h2l2.5 12h11l2-8H6" />
    </>
  ),
  orders: (
    <>
      <path d="M5 4h14v16H5z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </>
  ),
  home: <path d="M3 11l9-8 9 8v9a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1v-9z" />,
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M4 12.5l5 5L20 6.5" />,
  filter: <path d="M3 5h18l-7 8v6l-4 2v-8L3 5z" />,
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18-2.5-2.7-2.5-15.3 0-18z" />
    </>
  ),
  logout: <path d="M15 12H4m0 0l4-4m-4 4l4 4M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4" />,
  trash: <path d="M4 7h16M9 7V5h6v2m-8 0l1 13h8l1-13" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

/** Иконки категорий приходят из админки строкой — она может не совпасть с набором. */
export function isIconName(value: string | null | undefined): value is IconName {
  return Boolean(value && value in PATHS);
}

export default function Icon({
  name,
  size = 22,
  className = '',
  filled = false,
  stroke = 2,
}: {
  name: IconName;
  size?: number;
  className?: string;
  filled?: boolean;
  stroke?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}
